import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from './config.service';

@Injectable({ providedIn: 'root' })
export class CryptoService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private get baseUrl(): string { return `${this.config.apiEndPoint}admin/auth`; }

  private publicKeyPromise: Promise<CryptoKey> | null = null;

  /**
   * Encrypts the payload into a single base64 envelope: RSA-OAEP-encrypted AES
   * key (256 bytes) + AES-GCM IV (12 bytes) + auth tag (16 bytes) + ciphertext.
   * Fixed-size segments mean the backend can slice it apart without delimiters.
   */
  async encrypt(payload: unknown): Promise<string> {
    const publicKey = await this.getPublicKey();

    const aesKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(payload));

    const encrypted = new Uint8Array(
      await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, aesKey, encoded),
    );
    // Web Crypto appends the 16-byte GCM auth tag to the ciphertext; Node's crypto needs them split.
    const ciphertext = encrypted.slice(0, encrypted.length - 16);
    const authTag = encrypted.slice(encrypted.length - 16);

    const rawAesKey = await crypto.subtle.exportKey('raw', aesKey);
    const encryptedKey = new Uint8Array(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, rawAesKey));

    const envelope = new Uint8Array(encryptedKey.length + iv.length + authTag.length + ciphertext.length);
    let offset = 0;
    envelope.set(encryptedKey, offset); offset += encryptedKey.length;
    envelope.set(iv, offset); offset += iv.length;
    envelope.set(authTag, offset); offset += authTag.length;
    envelope.set(ciphertext, offset);

    return this.toBase64(envelope);
  }

  private getPublicKey(): Promise<CryptoKey> {
    if (!this.publicKeyPromise) {
      this.publicKeyPromise = this.fetchPublicKey().catch(err => {
        this.publicKeyPromise = null;
        throw err;
      });
    }
    return this.publicKeyPromise;
  }

  private async fetchPublicKey(): Promise<CryptoKey> {
    const res = await firstValueFrom(
      this.http.get<{ data: { publicKey: string } }>(`${this.baseUrl}/public-key`),
    );
    const der = this.pemToArrayBuffer(res.data.publicKey);
    return crypto.subtle.importKey('spki', der, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt']);
  }

  private pemToArrayBuffer(pem: string): ArrayBuffer {
    const base64 = pem.replace(/-----BEGIN PUBLIC KEY-----/, '').replace(/-----END PUBLIC KEY-----/, '').replace(/\s+/g, '');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  }

  private toBase64(buf: ArrayBuffer | Uint8Array): string {
    const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
}
